import { useEffect, useState } from 'react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import type { MyCharacter } from '../admin/assignments/types';

/**
 * Fetches the signed-in user's active character assignments from
 * `GET /assignments/me`. Returns the full character list (empty when none or
 * on error). Cancels the in-flight request on unmount / refreshKey change.
 */
export function useMyCharacters(refreshKey = 0): {
  characters: MyCharacter[];
  loading: boolean;
} {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [characters, setCharacters] = useState<MyCharacter[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const base = await discoveryApi.getBaseUrl('assignments');
      const res = await fetchApi.fetch(`${base}/me`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { characters: MyCharacter[] };
      if (!cancelled) {
        setCharacters(body.characters ?? []);
        setLoading(false);
      }
    })().catch(() => {
      if (!cancelled) {
        setCharacters([]);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, refreshKey]);

  return { characters, loading };
}
